// src/pages/ItemStock/components/ItemStockTable.js
import React, { useState } from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import { EyeOutlined } from "@ant-design/icons";
import "../../../components/common/bt.css";

const ItemStockTable = ({ printMode = false }) => {
  const { list, loading } = useSelector((state) => state.itemStock);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const formatQty = (val, item) => {
    if (!val) return `0 ${item.unit_symbol}`;

    if (item.conversion === 1) {
      return `${parseFloat(val).toFixed(2)} ${item.unit_symbol}`;
    }

    return `${Math.trunc(val / item.conversion)} ${item.unit_symbol}, ${
      val % item.conversion
    } ${item.base_unit_name}`;
  };

  const columns = [
    {
      title: "No.",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    { title: "Code", dataIndex: "item_code", width: 80 },
    { title: "Item Name", dataIndex: "item_name", width: 150 },
    { title: "Group", dataIndex: "group_name", width: 100 },
    { title: "Category", dataIndex: "category_name", width: 100 },

    {
      title: "Opening Stock",
      dataIndex: "opening_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Purchased",
      dataIndex: "purchase_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Production",
      dataIndex: "production_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Sold",
      dataIndex: "sale_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Consume",
      dataIndex: "consume_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Purchase Return",
      dataIndex: "purchase_return_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Sales Return",
      dataIndex: "sale_return_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Transfer",
      dataIndex: "transfer_out_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Received",
      dataIndex: "transfer_in_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      title: "Damage",
      dataIndex: "damage_qty",
      render: (val, item) => formatQty(val, item),
    },
    {
      // Mathematically identical to a closing stock: current_qty is
      // computed server-side (models/stock.js) as (opening + purchase +
      // production + sale_return + transfer_in) - (sale + purchase_return +
      // damage + consume + transfer_out) -- the standard opening-plus-
      // movements-minus-movements-equals-closing formula. This report is
      // also always "as of today" (the frontend never sends a toDate), so
      // it's never a point-in-time snapshot that "current" would describe
      // more accurately than "closing" -- renamed so it reads as the
      // counterpart to "Opening Stock" instead of looking like a different,
      // missing figure.
      title: "Closing Stock",
      dataIndex: "display_qty",
      render: (val) => <span style={{ fontWeight: "bold" }}>{val}</span>,
    },
    {
      title: "Costing Rate (AVG.)",
      dataIndex: "average_rate",
      render: (val, item) => (
        <>
          {parseFloat(val || 0).toFixed(2)}
          <br />
          <small>
            PER {item.conversion === 1 ? item.unit_symbol : item.base_unit_name}
          </small>
        </>
      ),
    },
    {
      title: "Stock Value",
      dataIndex: "stock_value",
      render: (val) => parseFloat(val || 0).toFixed(2),
    },
  ];

  const totals = list.reduce(
    (acc, row) => {
      acc.opening_qty += Number(row.opening_qty || 0);
      acc.purchase_qty += Number(row.purchase_qty || 0);
      acc.production_qty += Number(row.production_qty || 0);
      acc.sale_qty += Number(row.sale_qty || 0);
      acc.consume_qty += Number(row.consume_qty || 0);
      acc.purchase_return_qty += Number(row.purchase_return_qty || 0);
      acc.sale_return_qty += Number(row.sale_return_qty || 0);
      acc.transfer_out_qty += Number(row.transfer_out_qty || 0);
      acc.transfer_in_qty += Number(row.transfer_in_qty || 0);
      acc.damage_qty += Number(row.damage_qty || 0);
      acc.stock_value += Number(row.stock_value || 0);
      return acc;
    },
    {
      opening_qty: 0,
      purchase_qty: 0,
      production_qty: 0,
      sale_qty: 0,
      consume_qty: 0,
      purchase_return_qty: 0,
      sale_return_qty: 0,
      transfer_out_qty: 0,
      transfer_in_qty: 0,
      damage_qty: 0,
      stock_value: 0,
    }
  );

  if (printMode) {
  const cellStyle = {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    verticalAlign: "top",
  };
  const headStyle = { ...cellStyle, verticalAlign: "middle" };

  return (
    <table className="ledger-print-table item-stock-landscape">
      <colgroup>
        <col style={{ width: "4%" }} />
        <col style={{ width: "5%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "7%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "6%" }} />
        <col style={{ width: "7%" }} />
        <col style={{ width: "7%" }} />
      </colgroup>
      <tbody>
        <tr className="ledger-print-heading">
          <td style={headStyle}>No.</td>
          <td style={headStyle}>Code</td>
          <td style={headStyle}>Item Name</td>
          <td style={headStyle}>Group</td>
          <td style={headStyle}>Category</td>
          <td style={headStyle}>Opening</td>
          <td style={headStyle}>Purchased</td>
          <td style={headStyle}>Production</td>
          <td style={headStyle}>Sold</td>
          <td style={headStyle}>Consume</td>
          <td style={headStyle}>Purchase Return</td>
          <td style={headStyle}>Sales Return</td>
          <td style={headStyle}>Transfer</td>
          <td style={headStyle}>Received</td>
          <td style={headStyle}>Damage</td>
          <td style={headStyle}>Closing Stock</td>
          <td style={headStyle}>Costing Rate</td>
          <td style={headStyle}>Stock Value</td>
        </tr>

        {list.map((row, index) => (
          <tr key={row.item_id}>
            <td style={cellStyle}>{index + 1}</td>
            <td style={cellStyle}>{row.item_code}</td>
            <td style={cellStyle}>{row.item_name}</td>
            <td style={cellStyle}>{row.group_name}</td>
            <td style={cellStyle}>{row.category_name}</td>

            <td style={cellStyle}>{formatQty(row.opening_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.purchase_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.production_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.sale_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.consume_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.purchase_return_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.sale_return_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.transfer_out_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.transfer_in_qty, row)}</td>
            <td style={cellStyle}>{formatQty(row.damage_qty, row)}</td>

            <td style={cellStyle}>{row.display_qty}</td>

            <td style={{ ...cellStyle, textAlign: "right" }}>
              {parseFloat(row.average_rate || 0).toFixed(2)}
            </td>

            <td style={{ ...cellStyle, textAlign: "right" }}>
              {parseFloat(row.stock_value || 0).toFixed(2)}
            </td>
          </tr>
        ))}

        <tr className="ledger-total-row">
          <td colSpan={5} style={{ ...cellStyle, textAlign: "right" }}>
            Grand Total :
          </td>

          <td style={cellStyle}>{totals.opening_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.purchase_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.production_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.sale_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.consume_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.purchase_return_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.sale_return_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.transfer_out_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.transfer_in_qty.toFixed(2)}</td>
          <td style={cellStyle}>{totals.damage_qty.toFixed(2)}</td>

          <td style={cellStyle} />
          <td style={cellStyle} />

          <td style={{ ...cellStyle, textAlign: "right" }}>
            {totals.stock_value.toFixed(2)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

  return (
    <Table
      rowKey="item_id"
      columns={columns}
      dataSource={list}
      loading={loading}
      pagination={
        !printMode && {
          current: currentPage,
          pageSize: pageSize,
          total: list.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => setCurrentPage(page),
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }
      }
      bordered
      scroll={{ x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell colSpan={5}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>

          <Table.Summary.Cell>{totals.opening_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.purchase_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.production_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.sale_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.consume_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.purchase_return_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.sale_return_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.transfer_out_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.transfer_in_qty.toFixed(2)}</Table.Summary.Cell>
          <Table.Summary.Cell>{totals.damage_qty.toFixed(2)}</Table.Summary.Cell>

          <Table.Summary.Cell />
          <Table.Summary.Cell />

          <Table.Summary.Cell>
            <strong>{totals.stock_value.toFixed(2)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default ItemStockTable;